import type { Factura } from '../../components/facturas/types';
import type { FacturaFormState, TipoDescuento, MetodoEntregaForm } from '../../shared/nuevaFactura/facturaFormTypes';
import type { PaymentMethod } from '../../stores/usePaymentMethodsStore';
import { metodoPagoDbDesdeMetodoPago, paymentMethodTypeToFacturaDb } from './buildFacturaNuevaDesdeFormulario';
import { createEmptyFacturaFormState } from '../../shared/nuevaFactura/useFacturaForm';
import { METODO_PAGO_SISTEMA_CREDITO_ID } from './sistemaMetodoCredito';
import { facturaPagoCompletoDesdeFila } from '../../shared/normalizeEstadoPago';

function normalizeMetodoDb(s: string | undefined | null): string {
  return String(s ?? '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
}

/** Encuentra el `payment_methods.id` cuyo tipo coincide con `facturas.metodo_pago` en DB. */
export function matchPaymentMethodIdFromFacturaDb(
  metodoDb: string | undefined | null,
  methods: PaymentMethod[],
): string | null {
  const want = normalizeMetodoDb(metodoDb);
  if (!want) return null;
  if (want === 'CREDITO') return METODO_PAGO_SISTEMA_CREDITO_ID;
  for (const m of methods) {
    if (!m.is_active || m.show_on_invoices === false) continue;
    const mapped = normalizeMetodoDb(metodoPagoDbDesdeMetodoPago(m));
    if (mapped === want) return m.id;
  }
  for (const m of methods) {
    if (!m.is_active || m.show_on_invoices === false) continue;
    const k = normalizeMetodoDb(m.method_type);
    if (k === want) return m.id;
  }
  return null;
}

const COBRAR_AL_DESTINO_TAG = /\[cobrar_al_destino\]/i;

function parseNotasEntrega(raw: string | undefined | null): { notas: string; cobrarAlDestino: boolean } {
  const s = String(raw || '');
  const cobrarAlDestino = COBRAR_AL_DESTINO_TAG.test(s);
  const notas = s.replace(COBRAR_AL_DESTINO_TAG, '').trim();
  return { notas, cobrarAlDestino };
}

function mapTipoDescuento(td: Factura['tipoDescuento'] | undefined): TipoDescuento {
  if (td === 'PORCENTAJE') return 'porcentaje';
  if (td === 'MONTO_FIJO') return 'valor';
  return 'ninguno';
}

function clienteFacturaToForm(c: Factura['cliente']): FacturaFormState['cliente'] {
  if (!c) return null;
  const full = `${c.nombre || ''} ${c.apellido || ''}`.trim();
  return {
    id: c.id,
    nombre: full || c.nombre || 'Sin nombre',
    telefono: c.telefono,
    cedula: c.numeroDocumento,
    email: c.email,
  };
}

export function itemsFacturaToProductoLineas(items: Factura['items']): FacturaFormState['productos'] {
  return (items || []).map((it) => ({
    id: it.productoId || it.id,
    nombre: typeof it.producto === 'string' ? it.producto : (it.producto as { nombre?: string })?.nombre || 'Producto',
    precio: it.precioUnitario,
    qty: Math.max(1, it.cantidad || 1),
    iva_porcentaje: it.ivaPorcentaje,
  }));
}

/** `datetime-local` en hora local. */
export function fechaToDatetimeLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const h = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${y}-${m}-${day}T${h}:${min}`;
}

export function buildFormStateFromFactura(
  f: Factura,
  paymentMethodId: string,
): FacturaFormState {
  const base = createEmptyFacturaFormState();
  const fe = f.fechaEntrega ? f.fechaEntrega.toISOString().split('T')[0] : base.fecha_entrega;
  return {
    ...base,
    cliente: clienteFacturaToForm(f.cliente),
    numero_factura: (f.numero || base.numero_factura).trim(),
    fecha: f.fechaCreacion ? (f.fechaCreacion.toISOString().split('T')[0] ?? base.fecha) : base.fecha,
    productos: itemsFacturaToProductoLineas(f.items),
    descuento_tipo: mapTipoDescuento(f.tipoDescuento),
    descuento_valor: f.valorDescuento ?? 0,
    tipo_entrega: f.tipoEntrega === 'DOMICILIO' ? 'domicilio' : 'tienda',
    costo_envio: Math.max(0, Math.round(Number(f.costoEnvio) || 0)),
    metodo_entrega: ((): MetodoEntregaForm => {
      const m = String(f.metodoEntrega || '').toLowerCase();
      if (m === 'recogida' || m === 'entrega_local' || m === 'envio') return m;
      return f.tipoEntrega === 'DOMICILIO' ? 'entrega_local' : 'recogida';
    })(),
    ...(() => {
      const parsed = parseNotasEntrega(f.notasEntrega);
      return { notas_entrega: parsed.notas, cobrar_envio_al_destino: parsed.cobrarAlDestino };
    })(),
    fecha_entrega: fe ?? base.fecha_entrega,
    direccion_entrega: f.direccionEntrega || '',
    metodo_pago: paymentMethodId,
    estado_pago: facturaPagoCompletoDesdeFila(f) ? 'pagado' : 'pendiente',
    agendar_calendario: !!f.fechaEntregaAgendada || f.agendarCalendario === true,
    es_regalo: !!f.esRegalo,
    notas: f.notas || '',
    vendedor_id: f.vendedor && /^[0-9a-f-]{36}$/i.test(f.vendedor) ? f.vendedor : base.vendedor_id,
  };
}
